import { create } from "zustand";
import { devtools, subscribeWithSelector } from "zustand/middleware";
import { DEVTOOLS_ENABLED } from "../../../consts";
import type { TabId } from "./tabs";

interface NavBarState {
  /** The highlighted tab, or undefined when no tab matches. */
  activeNavBar: TabId | undefined;
  /** Mirrors NavBar's derived `hidden`, for the consumers that render outside it. */
  navBarHidden: boolean;
  /**
   * The bar's measured height in px, 0 while it is collapsed or not rendered at all.
   * NavBar publishes it from a ResizeObserver; `useTabBarBottomSafeOffset` is the reader.
   * It is a store field rather than a `navBarRef.current.offsetHeight` read because the
   * height animates — a consumer measuring during the expansion pins itself too low and,
   * having subscribed to nothing, never corrects.
   */
  navBarHeight: number;
  /** Blocks taps on every tab while an in-flight operation must not be interrupted. */
  tabBarDisabled: boolean;
  setActiveNavBar: (activeNavBar: TabId | undefined) => void;
  setNavBarHidden: (navBarHidden: boolean) => void;
  setNavBarHeight: (navBarHeight: number) => void;
  setTabBarDisabled: (tabBarDisabled: boolean) => void;
}

// `create` comes from "zustand" directly, not feat/appState/resetState: that wrapper
// registers the store for `resetAllStores()` on logout, and resetting `activeNavBar`
// mid-session would highlight the wrong tab. Not persisted either: both fields are
// derived from the current route on every navigation.
export const useNavBarStore = create<NavBarState>()(
  subscribeWithSelector(
    devtools(
      (set) => ({
        activeNavBar: undefined,
        navBarHidden: false,
        navBarHeight: 0,
        tabBarDisabled: false,
        setActiveNavBar: (activeNavBar) => set({ activeNavBar }, false, "setActiveNavBar"),
        setNavBarHidden: (navBarHidden) => set({ navBarHidden }, false, "setNavBarHidden"),
        // The observer fires on every frame of the 200ms expansion. Dropping the no-op
        // writes keeps that from waking eight subscribed layouts once per frame.
        setNavBarHeight: (navBarHeight) =>
          set((s) => (s.navBarHeight === navBarHeight ? s : { navBarHeight }), false, "setNavBarHeight"),
        setTabBarDisabled: (tabBarDisabled) => set({ tabBarDisabled }, false, "setTabBarDisabled"),
      }),
      { enabled: DEVTOOLS_ENABLED, name: "navBar" },
    ),
  ),
);

/// ˅˅˅ LEAF SELECTORS
// One hook per field, matching the per-signal granularity the signals had.

export const useActiveNavBar = () => useNavBarStore((s) => s.activeNavBar);
export const useNavBarHidden = () => useNavBarStore((s) => s.navBarHidden);
export const useNavBarHeight = () => useNavBarStore((s) => s.navBarHeight);
export const useTabBarDisabled = () => useNavBarStore((s) => s.tabBarDisabled);

/// ^^^ LEAF SELECTORS
